import { servicesDataEn, servicesDataAr } from "../data/servicesData";
import { useLanguage } from "../LanguageContext";

export default function ServicesView({ navigate }) {
  const { isAr } = useLanguage();
  const t = (en, ar) => (isAr ? ar : en);
  const servicesData = isAr ? servicesDataAr : servicesDataEn;

  return (
    <div className="view-enter min-h-screen flex flex-col">
      <div className="pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 bg-secondary-50 flex-grow">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-axion-base font-bold tracking-widest uppercase mb-2 text-sm font-display">
              {t("What We Do", "ما نقوم به")}
            </h3>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 font-display">
              {t("Our Services", "خدماتنا")}
            </h1>
            <h2 className="text-base sm:text-lg md:text-xl text-neutral-600 font-light max-w-xl md:max-w-2xl mx-auto font-display">
              {t(
                "Holistic solutions for complex railway infrastructure.",
                "حلول شاملة للبنية التحتية المعقدة للسكك الحديدية."
              )}
            </h2>
          </div>

          {/* Mobile: snap-scroll carousel */}
          <div className="sm:hidden -mx-4 px-4 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
            {Object.entries(servicesData).map(([key, data]) => (
              <div
                key={key}
                className="flex-shrink-0 w-[78vw] snap-start rounded-2xl overflow-hidden cursor-pointer relative h-72 shadow-xl"
                onClick={() => navigate("service-detail", key)}
              >
                <img src={data.image} alt={data.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-600/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                    <i className={`ph ${data.icon} text-white text-xl`} />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-white font-black text-lg leading-tight mb-1.5 font-display">{data.title}</h3>
                  <p className="text-white/75 text-xs leading-relaxed line-clamp-2">{data.shortDesc}</p>
                  <div className="mt-3 flex items-center gap-1.5 font-bold text-xs text-axion-gold">
                    {t("Discover More", "اكتشف المزيد")}
                    <i className={`ph-bold ${isAr ? "ph-arrow-left" : "ph-arrow-right"}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Swipe hint */}
          <p className="sm:hidden text-center text-xs text-neutral-400 mt-2 mb-2 flex items-center justify-center gap-1.5">
            <i className="ph ph-hand-swipe-right text-base" />
            {t("Swipe to explore", "اسحب للاستكشاف")}
          </p>

          {/* Tablet / Desktop: grid */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {Object.entries(servicesData).map(([key, data]) => (
              <ServiceCard key={key} serviceKey={key} data={data} navigate={navigate} isAr={isAr} />
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 sm:py-16 md:py-20 px-4 sm:px-6" style={{ backgroundColor: "rgb(17, 60, 111)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 font-display text-white">
            {t("Need a Custom Solution?", "هل تحتاج إلى حل مخصص؟")}
          </h2>
          <p className="text-white/90 mb-8 sm:mb-10 text-sm sm:text-base md:text-xl font-light">
            {t(
              "Our engineering team develops tailored solutions for your rail infrastructure requirements.",
              "يطور فريق المهندسين لدينا حلولاً مخصصة لمتطلبات بنيتك التحتية للسكك الحديدية."
            )}
          </p>
          <button
            onClick={() => navigate("contact")}
            className="bg-white text-primary-700 px-8 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-lg hover:bg-primary-700 hover:text-white shadow-button transition-all transform hover:-translate-y-1 inline-block w-full sm:w-auto"
          >
            {t("Contact Us", "تواصل معنا")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ serviceKey, data, navigate, isAr }) {
  const t = (en, ar) => (isAr ? ar : en);
  return (
    <div
      className="bg-white rounded-2xl shadow-soft interactive-card cursor-pointer group border border-neutral-200/50 flex flex-col h-full relative overflow-hidden hover:shadow-medium transition-all duration-300"
      onClick={() => navigate("service-detail", serviceKey)}
    >
      <div className="h-56 sm:h-64 w-full overflow-hidden bg-gray-100 relative">
        <img src={data.image} alt={data.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6 sm:p-8 flex-grow flex flex-col relative z-10 bg-white">
        <h3 className="text-xl sm:text-2xl font-black text-neutral-900 mb-3 sm:mb-4 font-display">{data.title}</h3>
        <p className="text-neutral-600 mb-8 font-light leading-relaxed flex-grow">{data.shortDesc}</p>
        <div className="pt-6 border-t border-neutral-200 mt-auto flex items-center justify-between">
          <span className="text-primary-700 font-bold text-sm tracking-wide uppercase group-hover:text-primary-800 transition-colors">
            {t("Discover More", "اكتشف المزيد")}
          </span>
          <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
            <i className={`ph-bold ${isAr ? "ph-arrow-left" : "ph-arrow-right"}`} />
          </div>
        </div>
      </div>
    </div>
  );
}
